import React, { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import "./ContactForm.css";

// Country codes for the dropdown
const countryCodes = [
  { name: "India", code: "+91" },
  { name: "USA / Canada", code: "+1" },
  { name: "UK", code: "+44" },
  { name: "Australia", code: "+61" },
  { name: "Germany", code: "+49" },
  { name: "France", code: "+33" },
  { name: "Japan", code: "+81" },
  { name: "China", code: "+86" },
  { name: "UAE", code: "+971" },
  { name: "Singapore", code: "+65" },
  { name: "Malaysia", code: "+60" },
  { name: "South Africa", code: "+27" },
  { name: "Brazil", code: "+55" },
  { name: "Netherlands", code: "+31" },
  { name: "Sweden", code: "+46" },
];

const ContactForm = ({
  // If you're showing the modal title from the Modal component, set this to false.
  showHeader = true,
  // Optional: close modal after success
  onSuccess,
  // Optional: allow parent to provide a close button action (not required)
  onClose,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "General Inquiry",
    countryCode: "+91",
    phone: "",
    message: "",
  });

  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errors, setErrors] = useState({});

  // If modal closes externally, keep things clean next time it opens
  useEffect(() => {
    if (status === "success") {
      const t = setTimeout(() => setStatus("idle"), 5000);
      return () => clearTimeout(t);
    }
  }, [status]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Full Name is required.";

    if (!formData.email.trim()) {
      newErrors.email = "Email Address is required.";
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      newErrors.email = "Email address is invalid.";
    }

    // Optional phone: if filled, must be digits only, length 7-15
    if (formData.phone && !/^\d{7,15}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number (digits only).";
    }

    if (!formData.message.trim()) newErrors.message = "Message is required.";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard: refuse to submit if EmailJS is not configured
    if (
      !process.env.REACT_APP_EMAILJS_SERVICE_ID ||
      !process.env.REACT_APP_EMAILJS_TEMPLATE_ID ||
      !process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    ) {
      setStatus("error");
      setErrors({ submit: "Contact form is not configured. Please email directly." });
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatus("idle");
      return;
    }

    setErrors({});
    setStatus("submitting");

    const payload = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
      phoneFull: formData.phone ? `${formData.countryCode} ${formData.phone}` : "",
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        payload,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setStatus("success");
      setFormData({
        name: "",
        email: "",
        subject: "General Inquiry",
        countryCode: "+91",
        phone: "",
        message: "",
      });

      // Optional: let parent close modal after success
      if (onSuccess) {
        setTimeout(() => onSuccess(), 800);
      }
    } catch (err) {
      console.error("EmailJS failed:", err);
      setStatus("error");
      setErrors({ submit: "Something went wrong. Please try again later." });
    }
  };

  const isDisabled = status === "submitting";

  return (
    // ✅ Prevent Lenis from hijacking scroll inside modal/form
    <div className="form-container" data-lenis-prevent>
      {/* Optional inline close button if you want it inside form */}
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close form"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            color: "#fff",
            fontSize: 28,
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ×
        </button>
      ) : null}

      {showHeader && (
        <>
          <h2>Get in Touch</h2>
          <p className="form-intro">
            Have a question or a project in mind? Fill out the form below.
          </p>
        </>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="name">Full Name</label>
          <input
            name="name"
            type="text"
            id="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your Name"
            autoComplete="name"
            disabled={isDisabled}
            className={errors.name ? "input-error" : ""}
          />
          {errors.name && <p className="error-text">{errors.name}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            name="email"
            type="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            autoComplete="email"
            disabled={isDisabled}
            className={errors.email ? "input-error" : ""}
          />
          {errors.email && <p className="error-text">{errors.email}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <div className="phone-group">
            <div className="select-wrapper">
              <select
                name="countryCode"
                id="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                disabled={isDisabled}
              >
                {countryCodes.map((country) => (
                  <option key={country.name} value={country.code}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </select>
            </div>

            <input
              name="phone"
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="9876543210"
              inputMode="numeric"
              autoComplete="tel"
              disabled={isDisabled}
              className={errors.phone ? "input-error" : ""}
            />
          </div>

          {errors.phone && <p className="error-text">{errors.phone}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="subject">Subject</label>
          <div className="select-wrapper">
            <select
              name="subject"
              id="subject"
              value={formData.subject}
              onChange={handleChange}
              disabled={isDisabled}
            >
              <option>General Inquiry</option>
              <option>Project Proposal</option>
              <option>Collaboration</option>
              <option>Feedback</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="message">Message</label>
          <textarea
            name="message"
            id="message"
            rows="5"
            value={formData.message}
            onChange={handleChange}
            placeholder="Your message here..."
            disabled={isDisabled}
            className={errors.message ? "input-error" : ""}
          />
          {errors.message && <p className="error-text">{errors.message}</p>}
        </div>

        {status === "success" && (
          <div role="status" aria-live="polite" className="status-message success-message">
            Thanks! We&#39;ve received your message and will get back to you shortly.
          </div>
        )}

        {status === "error" && errors.submit && (
          <div role="alert" aria-live="assertive" className="status-message error-message">{errors.submit}</div>
        )}

        <button type="submit" className="submit-btn" disabled={isDisabled}>
          {isDisabled ? "Sending..." : "Send Message"}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
